import { useEffect, useMemo, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { parseAbiItem } from 'viem'
import { IS_LIVE } from '@/lib/contracts'
import { useVaultsList } from '@/hooks/useVaultsList'

const SECONDS_PER_YEAR = 365 * 86400
// A short window doesn't just risk "not enough data" - annualizing it actively misleads: a single
// noisy trade round-trip inside a 1-hour sample compounds ~8,760x into wild, often negative,
// swings that have nothing to do with the vault's real performance (confirmed directly against
// this app's own local dev-net demo history - a few hours of real but noisy activity produced a
// -40% "APY" that settled down once judged over a realistic window). 24h is the shortest window
// that averages out single-trade noise, matching the trailing-window convention real yield
// aggregators use for exactly this reason.
const MIN_ELAPSED_SEC = 24 * 3600
const LOOKBACK_SEC = 7 * 86400 // trailing 7-day window, or all available history if shorter

// ~9 days of mainnet blocks (12s each), so the trailing window plus its 24h minimum always fits.
// On a fork the chain is younger than this and getLogs clamps to genesis on its own.
const BLOCKS_LOOKBACK = 65_000n
const REFRESH_MS = 120_000

/// NAV-per-share history read STRAIGHT OFF THE CHAIN: every vault emits `NavCheckpoint` at each
/// fee realisation (deposit, compound, rebalance - CHANGELOG.md entry 203), so this hook is one
/// `getLogs` over the vault set instead of a fetch of a file some scheduled indexer may or may
/// not have refreshed. The indexer this replaces failed exactly that way twice in one day: silent
/// staleness, then dead addresses after a redeploy. The chain cannot do either.
///
/// apyFor(address) derives a real trailing APY: geometric annualization of NAV-per-share growth
/// between the oldest checkpoint inside the trailing LOOKBACK_SEC window and the latest one.
/// Returns null (render as "—", the same "not yet knowable" convention VaultsSection.jsx/
/// VaultDetailSection.jsx already use) until at least two checkpoints spanning MIN_ELAPSED_SEC of
/// real time exist - a single point, or two points seconds apart, would amplify noise into an
/// absurd annualized number rather than an honest one.
///
/// Weekly volume comes from DexScreener directly (the one figure that is about the POOL, not the
/// vault, so the chain cannot answer it): null on chains DexScreener does not cover, which is
/// every local fork - rendered "—", never fabricated.
const NAV_EVENT = parseAbiItem('event NavCheckpoint(uint256 navPerShare)')

export function useVaultNavHistory() {
  const client = usePublicClient()
  const { vaults } = useVaultsList()
  const [data, setData] = useState(null)
  const [volumes, setVolumes] = useState({})
  const [loading, setLoading] = useState(true)

  const addressKey = useMemo(
    () => vaults.map((v) => v.address.toLowerCase()).sort().join(','),
    [vaults],
  )

  useEffect(() => {
    if (!IS_LIVE || !client || addressKey.length === 0) { setLoading(false); return }
    let cancelled = false

    async function load() {
      try {
        const addresses = addressKey.split(',')
        const latest = await client.getBlockNumber()
        const fromBlock = latest > BLOCKS_LOOKBACK ? latest - BLOCKS_LOOKBACK : 0n
        const logs = await client.getLogs({ address: addresses, event: NAV_EVENT, fromBlock, toBlock: latest })

        // One timestamp fetch per UNIQUE block - checkpoints cluster on activity, so this is a
        // handful of getBlock calls, not one per log.
        const blockNums = [...new Set(logs.map((l) => l.blockNumber))]
        const stamps = new Map()
        await Promise.all(blockNums.map(async (bn) => {
          const b = await client.getBlock({ blockNumber: bn })
          stamps.set(bn, Number(b.timestamp))
        }))

        const series = {}
        for (const log of logs) {
          const addr = log.address.toLowerCase()
          ;(series[addr] ??= []).push({ t: stamps.get(log.blockNumber), nav: Number(log.args.navPerShare) / 1e18 })
        }
        for (const addr of Object.keys(series)) series[addr].sort((a, b) => a.t - b.t)
        if (!cancelled) setData(series)
      } catch {
        if (!cancelled) setData(null) // RPC hiccup: columns show "—" rather than a stale guess
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    const id = setInterval(load, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [client, addressKey])

  // DexScreener 24h volume per vault, matched by the vault's own token pair on this chain. One
  // fetch per token0 (the API returns every pair for a token), refreshed with the log poll.
  useEffect(() => {
    if (!IS_LIVE || vaults.length === 0) return
    let cancelled = false

    async function loadVolumes() {
      const out = {}
      const byToken0 = new Map()
      for (const v of vaults) {
        const t0 = v.token0?.address?.toLowerCase()
        if (t0) (byToken0.get(t0) ?? byToken0.set(t0, []).get(t0)).push(v)
      }
      await Promise.all([...byToken0.entries()].map(async ([t0, vs]) => {
        try {
          const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${t0}`)
          if (!res.ok) return
          const json = await res.json()
          for (const v of vs) {
            const t1 = v.token1?.address?.toLowerCase()
            const pairs = (json.pairs ?? []).filter((p) =>
              p.chainId === 'ethereum' &&
              [p.baseToken?.address?.toLowerCase(), p.quoteToken?.address?.toLowerCase()].includes(t1))
            if (pairs.length === 0) continue
            // Deepest pair carries the pair's real volume story.
            pairs.sort((a, b) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0))
            const vol = pairs[0].volume?.h24
            if (typeof vol === 'number') out[v.address.toLowerCase()] = vol
          }
        } catch { /* no coverage (every local fork): stays "—" */ }
      }))
      if (!cancelled) setVolumes(out)
    }

    loadVolumes()
    const id = setInterval(loadVolumes, REFRESH_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [vaults, addressKey])

  function apyFor(address) {
    if (!data || !address) return null
    const series = data[address.toLowerCase()]
    if (!series || series.length < 2) return null
    return apyAt(series, series.length - 1)
  }

  /// Rolling APY at every point in the series (for the "APY" historical-rate tab) - the same
  /// trailing-window annualization apyFor uses, just anchored at each point instead of only the
  /// latest one. Points too early to have MIN_ELAPSED_SEC of real history behind them are
  /// dropped entirely, not zero-filled or interpolated - a chart with fewer early points is
  /// honest; a chart with fabricated flat/zero values at the start is not.
  function apySeriesFor(address) {
    if (!data || !address) return []
    const series = data[address.toLowerCase()]
    if (!series || series.length < 2) return []
    const out = []
    for (let i = 1; i < series.length; i++) {
      const apy = apyAt(series, i)
      if (apy != null) out.push({ t: series[i].t, apy })
    }
    return out
  }

  /// Real USD fees a vault earned its depositors over the trailing 7 days: NAV-per-share growth
  /// across the window times the vault's CURRENT position value (from the live list, the same
  /// figure the TVL column shows). Same honesty rule as apyFor: null until the window is real.
  function weeklyFeesUsdFor(address) {
    if (!data || !address) return null
    const series = data[address.toLowerCase()]
    if (!series || series.length < 2) return null
    const tvlUsd = vaults.find((v) => v.address.toLowerCase() === address.toLowerCase())?.tvlUsd
    if (!(tvlUsd > 0)) return null
    const latest = series[series.length - 1]
    const cutoff = latest.t - LOOKBACK_SEC
    let oldest = series[0]
    for (let i = 0; i < series.length; i++) {
      if (series[i].t >= cutoff) { oldest = series[i]; break }
    }
    const elapsed = latest.t - oldest.t
    if (elapsed < MIN_ELAPSED_SEC || oldest.nav <= 0 || latest.nav <= 0) return null
    const growth = latest.nav / oldest.nav - 1
    return growth * (LOOKBACK_SEC / elapsed) * tvlUsd
  }

  /// Trailing-7-day volume: DexScreener's rolling 24h figure for the vault's pair, times seven.
  function weeklyVolumeUsdFor(address) {
    const vol = address ? volumes[address.toLowerCase()] : undefined
    return typeof vol === 'number' ? vol * 7 : null
  }

  /// Shared core: geometric-annualizes NAV growth between the oldest checkpoint inside the
  /// trailing LOOKBACK_SEC window (ending at series[endIdx]) and series[endIdx] itself.
  function apyAt(series, endIdx) {
    const latest = series[endIdx]
    const cutoff = latest.t - LOOKBACK_SEC
    let oldest = series[0]
    for (let i = 0; i <= endIdx; i++) {
      if (series[i].t >= cutoff) { oldest = series[i]; break }
    }

    const elapsed = latest.t - oldest.t
    if (elapsed < MIN_ELAPSED_SEC || oldest.nav <= 0 || latest.nav <= 0) return null

    const growth = latest.nav / oldest.nav
    return (growth ** (SECONDS_PER_YEAR / elapsed) - 1) * 100
  }

  return { data, loading, apyFor, apySeriesFor, weeklyFeesUsdFor, weeklyVolumeUsdFor }
}
